<script setup lang="ts">
import { defineEmits, defineProps, onMounted, onUnmounted, ref, watch, nextTick, computed } from 'vue';
import { EMIT } from '@/enum';

const emit = defineEmits<(event: EMIT.CLOSE, time: number) => void>();

const props = withDefaults(
  defineProps<{
    stream: MediaStream | null;
    fftSize?: number;
    smoothing?: number;
    barWidth?: number;
    gap?: number;
    color?: string;
    background?: string;
  }>(),
  {
    fftSize: 256,
    smoothing: 0.8,
    barWidth: 4,
    gap: 2,
    color: '#078888',
    background: '#fff',
  },
);

const rootEl = ref<HTMLElement | null>(null);
const canvasEl = ref<HTMLCanvasElement | null>(null);

let audioCtx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let dataArray: Uint8Array | null = null;
let rafId = 0;

const startTs = ref<number | null>(null);
const elapsedMs = ref(0);
const elapsedLabel = computed(() => {
  const totalSec = Math.floor((elapsedMs.value || 0) / 1000);
  const m = Math.floor(totalSec / 60)
    .toString()
    .padStart(2, '0');
  const s = (totalSec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
});

function setupAudio(stream: MediaStream) {
  cleanupAudio();

  audioCtx = new AudioContext();
  const source = audioCtx.createMediaStreamSource(stream);
  analyser = audioCtx.createAnalyser();
  analyser.fftSize = props.fftSize;
  analyser.smoothingTimeConstant = props.smoothing;
  dataArray = new Uint8Array(analyser.frequencyBinCount);
  source.connect(analyser);

  startTs.value = performance.now();
  elapsedMs.value = 0;

  nextTick(sizeCanvasAndDraw);
}

function sizeCanvasAndDraw() {
  const c = canvasEl.value;
  if (!c) return;
  c.width = c.offsetWidth || 300;
  c.height = c.offsetHeight || 60;

  cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(draw);
}

function draw() {
  const c = canvasEl.value;
  const a = analyser;
  const data = dataArray;
  if (!c || !a || !data) {
    rafId = requestAnimationFrame(draw);
    return;
  }
  const ctx = c.getContext('2d');
  if (!ctx) {
    rafId = requestAnimationFrame(draw);
    return;
  }

  if (startTs.value !== null) {
    elapsedMs.value = Math.max(0, performance.now() - startTs.value);
  }

  a.getByteFrequencyData(data);

  const WIDTH = c.width;
  const HEIGHT = c.height;

  ctx.clearRect(0, 0, WIDTH, HEIGHT);
  ctx.fillStyle = props.background;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  const barWidth = props.barWidth;
  const gap = props.gap;
  const maxBars = Math.max(1, Math.floor(WIDTH / (barWidth + gap)));
  const step = Math.max(1, Math.floor(data.length / maxBars));

  let x = 0;
  for (let i = 0; i < data.length; i += step) {
    let sum = 0;
    for (let j = 0; j < step && i + j < data.length; j++) sum += data[i + j];
    const avg = sum / step;
    const barHeight = (avg / 255) * HEIGHT;

    ctx.fillStyle = props.color;
    ctx.fillRect(x, HEIGHT - barHeight, barWidth, barHeight);

    x += barWidth + gap;
    if (x > WIDTH) break;
  }

  rafId = requestAnimationFrame(draw);
}

function cleanupAudio() {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = 0;
  analyser = null;
  dataArray = null;
  startTs.value = null;
  elapsedMs.value = 0;

  if (audioCtx) {
    audioCtx.close();
    audioCtx = null;
  }
}

watch(
  () => props.stream,
  (s) => {
    if (s) setupAudio(s);
    else cleanupAudio();
  },
  { immediate: true },
);

onMounted(() => {
  sizeCanvasAndDraw();
});

onUnmounted(() => {
  const duration = Math.floor((elapsedMs.value || 0) / 1000);
  emit(EMIT.CLOSE, duration);
  cleanupAudio();
});
</script>

<template>
  <div ref="rootEl" class="visualizer-root">
    <canvas ref="canvasEl" class="visualizer-canvas"></canvas>

    <div class="viz-timer" aria-live="polite">{{ elapsedLabel }}</div>
  </div>
</template>

<style scoped>
.visualizer-root {
  position: relative;
  width: 100%;
  height: 1.5rem;
}
.visualizer-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.viz-timer {
  position: absolute;
  top: 2px;
  right: 6px;
  padding: 2px 6px;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 4px;
  user-select: none;
}
</style>
